import React, { useState } from "react";
import { Box, Typography } from "@mui/material";

const LEVEL_COLORS = {
  queue: "#3b82f6",
  task: "#9ca3af",
};

const KIND_TITLE_COLORS = {
  completed: "#15803d",
  done: "#15803d",
  failed: "#dc2626",
  blocked: "#d97706",
  cancelled: "#6b7280",
  skipped: "#7c3aed",
  started: "#1d4ed8",
  created: "#3b82f6",
  planned: "#6b7280",
  recovered: "#15803d",
};

function titleColor(title) {
  const lower = String(title || "").toLowerCase();
  for (const [key, color] of Object.entries(KIND_TITLE_COLORS)) {
    if (lower.includes(key)) return color;
  }
  return "var(--color-text-primary)";
}

function formatRelativeTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d ago`;
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export default function TaskQueueTimeline({ events }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
      <Box
        onClick={() => setExpanded((v) => !v)}
        sx={{
          px: 1.5,
          py: 0.75,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
          userSelect: "none",
          "&:hover": { backgroundColor: "action.hover" },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <Typography sx={{ fontSize: "10px", color: "text.disabled", lineHeight: 1 }}>
            {expanded ? "▾" : "▸"}
          </Typography>
          <Typography sx={{ fontSize: "12px", fontWeight: 700, color: "text.primary" }}>
            Timeline
          </Typography>
          {events.length > 0 && (
            <Typography sx={{ fontSize: "10px", color: "text.disabled" }}>
              {events.length}
            </Typography>
          )}
        </Box>
        <Typography sx={{ fontSize: "10px", color: "text.disabled" }}>
          {expanded ? "Collapse" : "Expand"}
        </Typography>
      </Box>
      {expanded && (
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            maxHeight: "220px",
            overflowY: "auto",
            overflowX: "hidden",
            px: 1.5,
            py: 1,
            backgroundColor: "background.default",
            borderTop: "1px solid",
            borderTopColor: "divider",
          }}
        >
          {events.length === 0 ? (
            <Typography sx={{ fontSize: "11px", color: "text.disabled", py: 1 }}>
              Activity will appear here as tasks transition.
            </Typography>
          ) : (
            events.slice(0, 30).map((event) => {
              const borderColor = LEVEL_COLORS[event.kind] || "#e5e7eb";
              const tColor = titleColor(event.title);
              return (
                <Box
                  key={event.id}
                  sx={{
                    borderLeft: `2px solid ${borderColor}`,
                    pl: 1.25,
                    py: 0.5,
                    mb: 1,
                    "&:last-child": { mb: 0 },
                  }}
                >
                  <Typography sx={{ fontSize: "12px", fontWeight: 600, color: tColor, lineHeight: 1.3 }}>
                    {event.title}
                  </Typography>
                  {event.detail && (
                    <Typography
                      sx={{
                        fontSize: "11px",
                        color: "#6b7280",
                        mt: 0.25,
                        lineHeight: 1.3,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {event.detail}
                    </Typography>
                  )}
                  <Typography sx={{ fontSize: "10px", color: "#9ca3af", mt: 0.25 }}>
                    {formatRelativeTime(event.timestamp)}
                  </Typography>
                </Box>
              );
            })
          )}
        </Box>
      )}
    </Box>
  );
}
